import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Space,
  Stack,
} from '@tonic-one/react';
import { Parser } from '@json2csv/plainjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useQuarantineStore } from '@Quarantine/store';
import { getTimeRange } from '@Log/shared';
import { RiskAiDetection } from '../types';
import { useOrgStore } from '@Models/useOrgModel';
import { queryClient } from 'src/QueryClient';
import { InfiniteData } from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import { LogTypeEnum } from '@Types/log';
import { useDetectionTableQueryKey } from '../DetectionsTable/useDetectionTableQueryKey';

interface Props {
  visible: boolean;
  onClose: () => void;
  columns: { id: string; header: string }[];
  renderData: (contentList: RiskAiDetection[]) => RiskAiDetection[];
}

const ExportModal = (props: Props) => {
  const { visible, onClose, columns, renderData } = props;
  const { orgId } = useOrgStore();
  const [exportType, setExportType] = useState('');
  const [exportBtnDisabled, setExportBtnDisabled] = useState(true);
  const { t } = useTranslation('log');
  const {
    sortOrder,
    exportDetectionAsync,
    filters: {
      serviceFilter: { currentValue: serviceName },
      riskTypeFilter: { currentValue: riskType },
      timeFilter,
    },
  } = useQuarantineStore((store) => store.riskAi);
  const queryKey = useDetectionTableQueryKey({ sortOrder });

  const onRadioChanged = (value: string) => {
    setExportType(value);
    setExportBtnDisabled(false);
  };

  const reset = () => {
    setExportType('');
    setExportBtnDisabled(true);
  };

  const closeModal = () => {
    onClose();
    reset();
  };

  const exportCSVFile = (data: any) => {
    const csv = new Parser().parse(data);
    const url = window.URL.createObjectURL(
      new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = 'result.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const parseData = (data: any[]) => {
    const exportData = [];
    const emptyData: { [type: string]: string } = {};
    for (const column of columns) {
      emptyData[column.header] = '';
    }
    for (const item of data) {
      const rowData = { ...emptyData };
      for (const column of columns) {
        rowData[column.header] = item[column.id];
      }
      exportData.push(rowData);
    }
    if (exportData.length === 0) {
      exportData.push(emptyData);
    }
    return exportData;
  };

  const exportLog = async () => {
    closeModal();
    try {
      let renderedData: any = [];

      if (exportType === '0') {
        const data = queryClient.getQueriesData({
          queryKey,
          type: 'active',
        });
        const allPagesData =
          (
            data?.[0]?.[1] as
              | InfiniteData<{
                  data: RiskAiDetection[] | null;
                  total: number;
                }>
              | undefined
          )?.pages?.flatMap((page) => page?.data) ?? [];
        const allDetections = allPagesData.filter(
          (d) => !isNil(d)
        ) as RiskAiDetection[];
        renderedData = renderData(allDetections);
        const csvData = parseData(renderedData);
        exportCSVFile(csvData);
      } else {
        exportDetectionAsync({
          timeRange:
            timeFilter.custom ??
            getTimeRange(timeFilter.currentValue, LogTypeEnum.DETECTION),
          timestampSort: 'desc',
          orgId,
          serviceName,
          riskType,
        });
        // const res = await fetchDetections({
        //   page: { start: 1, size: 10000 },
        //   timeRange: getTimeRange(LogTimeUnit.All, LogTypeEnum.DETECTION),
        //   timestampSort: 'desc',
        //   riskType,
        //   orgId,
        //   serviceName: QuarantineRiskAILogTypeEnum.All,
        // });
        // renderedData = renderData(res.data.detections ?? []);
      }
    } catch (error) {
      window.showNotification({
        titleMsg: t('errors.log.export.title'),
        bodyMsg: t('errors.log.export.body'),
        severity: 'error',
      });
      console.error(error);
    }
  };

  return (
    <Modal
      closeOnEsc
      closeOnOutsideClick
      isClosable
      isOpen={visible}
      onClose={closeModal}
      size="sm"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('riskAi.model.exportData.title')}</ModalHeader>
        <ModalBody>
          <Box>
            <RadioGroup value={exportType} onChange={onRadioChanged}>
              <Stack spacing="1x" shouldWrapChildren>
                <Radio value="0">{t('log.modals.export.currentView')}</Radio>
                <Radio value="1">
                  {t('log.modals.export.allRecordsNoMaximum')}
                </Radio>
              </Stack>
            </RadioGroup>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button
            minWidth="20x"
            variant="primary"
            disabled={exportBtnDisabled}
            onClick={exportLog}
          >
            {t('log.button.export')}
          </Button>
          <Space width="2x" />
          <Button minWidth="20x" onClick={closeModal}>
            {t('log.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
export default ExportModal;
